import { dataURLtoFile } from './fileUtils'

/**
 * 여러 장을 한 번에 고른 사진을 허용 비율로 맞춘다.
 *
 * 왜 필요한가
 * ----------
 * 사진을 한 장만 고르면 크롭 화면(/photo-edit)으로 넘어가 1:1 · 4:5 · 5:4 중
 * 하나를 고르게 된다. 그런데 여러 장을 한 번에 고르면 그 화면을 건너뛰고
 * 원본이 그대로 올라갔다. 그래서 9:16 같은 세로로 긴 사진이 섞여 들어왔고,
 * 피드는 4:5 칸에 그리므로 위아래에 여백이 생겼다.
 *
 * 장마다 크롭 화면을 띄우면 열 장을 고를 때 열 번을 지나야 한다. 여러 장
 * 선택을 만든 이유가 그 번거로움을 없애려던 것이라 취지에 어긋난다.
 * 그래서 손대지 않고 기본 비율(4:5)로 가운데를 잘라 넣는다.
 *
 * 이미 허용 비율이면 그대로 둔다 — 다시 그릴 이유가 없고, 화질만 한 번 더 깎인다.
 * 마음에 안 들면 그 사진만 눌러 다시 편집하면 된다(PhotoEdit 재편집 경로).
 */

/** 크롭 화면이 제공하는 비율. 이 중 하나면 자르지 않는다. */
const ALLOWED_ASPECTS = [1, 4 / 5, 5 / 4]

/** 여기서 벗어나면 자른다. 사진마다 반올림 오차가 있어 딱 맞기를 기대할 수 없다. */
const ASPECT_TOLERANCE = 0.02

/** 허용 비율이 아닐 때 맞출 기본 비율 (세로 4:5) */
const DEFAULT_ASPECT = 4 / 5

/** iOS WKWebView 등에서 초대형 캔버스면 toDataURL이 data:, 로 비는 경우가 있어 상한 둠 */
const MAX_EXPORT_SIDE = 4096

/** data URL이 실제 이미지 내용을 담고 있는지 (빈 'data:,' 방어) */
function isNonEmptyImageDataUrl(dataUrl: string): boolean {
  return dataUrl.startsWith('data:image/') && dataUrl.length > 'data:image/png;base64,'.length
}

/** 이미 크롭 화면이 주는 비율 중 하나인가 */
function isAllowedAspect(aspect: number): boolean {
  return ALLOWED_ASPECTS.some(
    (allowed) => Math.abs(aspect - allowed) <= ASPECT_TOLERANCE
  )
}

/**
 * 사진 하나를 허용 비율로 맞춘다.
 *
 * 자를 필요가 없거나 자르지 못하면 **원본을 그대로 돌려준다**. 사진을 잃는 것보다
 * 비율이 어긋나는 편이 낫다.
 *
 * @param file 원본 파일
 * @param preview 원본 미리보기 (data URL)
 * @returns 맞춘 파일과 미리보기. 손대지 않았으면 받은 그대로.
 */
export async function autoCropToAllowedAspect(
  file: File,
  preview: string
): Promise<{ file: File; preview: string; cropped: boolean }> {
  const unchanged = { file, preview, cropped: false }

  let image: HTMLImageElement
  try {
    image = await loadImage(preview)
  } catch {
    return unchanged
  }

  const width = image.naturalWidth
  const height = image.naturalHeight
  if (!width || !height) {
    return unchanged
  }

  if (isAllowedAspect(width / height)) {
    return unchanged
  }

  // 가운데를 기준으로 4:5가 되도록 잘라낼 영역을 구한다.
  // 원본보다 큰 영역을 요구할 수 없으므로, 한 변을 원본에 맞추고 다른 변을 줄인다.
  let cropW = width
  let cropH = Math.round(width / DEFAULT_ASPECT)
  if (cropH > height) {
    cropH = height
    cropW = Math.round(height * DEFAULT_ASPECT)
  }

  const sx = Math.max(0, Math.round((width - cropW) / 2))
  const sy = Math.max(0, Math.round((height - cropH) / 2))

  // 지나치게 큰 캔버스는 내보내기가 실패하므로 긴 변 기준으로 줄인다.
  let outW = cropW
  let outH = cropH
  if (outW > MAX_EXPORT_SIDE || outH > MAX_EXPORT_SIDE) {
    const ratio = Math.min(MAX_EXPORT_SIDE / outW, MAX_EXPORT_SIDE / outH)
    outW = Math.max(1, Math.floor(outW * ratio))
    outH = Math.max(1, Math.floor(outH * ratio))
  }

  const canvas = document.createElement('canvas')
  canvas.width = outW
  canvas.height = outH

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    return unchanged
  }
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(image, sx, sy, cropW, cropH, 0, 0, outW, outH)

  // PhotoEdit과 같은 순서로 내보낸다(JPEG 실패 시 PNG).
  let base64 = canvas.toDataURL('image/jpeg', 0.92)
  if (!isNonEmptyImageDataUrl(base64)) {
    base64 = canvas.toDataURL('image/png')
  }
  if (!isNonEmptyImageDataUrl(base64)) {
    return unchanged
  }

  return {
    file: dataURLtoFile(base64, file.name),
    preview: base64,
    cropped: true,
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('image load failed'))
    image.src = src
  })
}
